/**
 * Custom Node Migration Utilities
 * Converts CustomNodeDefinition to UniversalNodeDefinition format
 */

import type { CustomNodeDefinition } from '../types/customNode';
import type { UniversalNodeDefinition } from '../components/nodes';
import CustomNodeStorage from '../services/CustomNodeStorage';
import logger from './logger';

/**
 * Convert a CustomNodeDefinition to UniversalNodeDefinition format
 */
export function convertCustomNodeToUniversal(
  customDef: CustomNodeDefinition
): UniversalNodeDefinition {
  // Determine variant based on category
  const variant = customDef.category === 'prompt' ? 'purple' : 'neutral';
  
  // Map inputs to acceptsMediaTypes (unique types only)
  const acceptsMediaTypes = customDef.inputs.length > 0
    ? Array.from(new Set(customDef.inputs.map(inp => inp.type)))
    : undefined;
  
  // Map outputs to outputsMediaType (use first output)
  const outputsMediaType = customDef.outputs.length > 0
    ? customDef.outputs[0].type
    : 'text';
  
  // Build component definitions
  const components: any[] = [
    {
      id: 'prompt-template',
      type: 'PromptTemplate',
      valueKey: 'variables',      // Bind to variables from inputs
      // Note: PromptTemplate uses onExecute, not onSubmit
      props: {
        template: customDef.promptTemplate,
        aiModel: customDef.aiModel,
        hasAdditionalInstructions: customDef.hasAdditionalInstructions,
        additionalInstructionsLabel: customDef.additionalInstructionsLabel || 'Additional Instructions',
        variant,
        nodeId: '', // Will be set by adapter
        parameters: {
          ...(customDef.aspectRatio && { aspectRatio: customDef.aspectRatio }),
          ...(customDef.durationSeconds && { duration: customDef.durationSeconds })
        }
      }
    }
  ];
  
  // Add ParameterControls for model-specific parameters
  const isImageModel = customDef.aiModel === 'nano-banana';
  const isVideoModel = customDef.aiModel === 'veo-3.1-fast' || customDef.aiModel === 'veo-3.0-fast';
  
  if (isImageModel || isVideoModel) {
    const parameters: any[] = [
      {
        id: 'aspectRatio',
        type: 'select',
        label: 'Aspect Ratio',
        options: [
          { value: '9:16', label: '9:16 (Portrait)' },
          { value: '1:1', label: '1:1 (Square)' },
          { value: '4:5', label: '4:5' },
          { value: '16:9', label: '16:9 (Landscape)' },
          { value: '4:3', label: '4:3' },
          { value: '3:4', label: '3:4' },
          { value: '3:2', label: '3:2' },
          { value: '2:3', label: '2:3' },
          { value: '5:4', label: '5:4' }
        ],
        defaultValue: customDef.aspectRatio || '9:16'
      }
    ];
    
    if (isVideoModel) {
      parameters.push({
        id: 'duration',
        type: 'select',
        label: 'Duration',
        options: [
          { value: '4', label: '4 seconds' },
          { value: '6', label: '6 seconds' },
          { value: '8', label: '8 seconds' }
        ],
        defaultValue: customDef.durationSeconds || '8'
      });
    }
    
    components.push({
      id: 'parameters',
      type: 'ParameterControls',
      valueKey: 'parameters',
      onChangeKey: 'onParameterChange',
      props: {
        variant,
        parameters
      }
    });
  }
  
  return {
    id: customDef.id,
    type: customDef.name.replace(/\s+/g, ''), // Remove spaces for type name
    title: customDef.name,
    category: 'processor',
    variant,
    acceptsMediaTypes,
    outputsMediaType,
    maxInputConnections: customDef.inputs.length || undefined,
    hasInputHandle: customDef.inputs.length > 0,
    hasOutputHandle: customDef.outputs.length > 0,
    components,
    
    // Store original custom node metadata for backwards compatibility and UI customization
    _customNodeMeta: {
      icon: customDef.icon,
      color: customDef.color,
      inputs: customDef.inputs,
      outputs: customDef.outputs,
      originalDefinition: customDef
    } as any
  };
}

/**
 * Check if a custom node has been migrated to UniversalNode format
 */
export function isCustomNodeMigrated(customDef: any): boolean {
  return !!(customDef._migrated && customDef._universalDefinition);
}

/**
 * Get the UniversalNodeDefinition from a migrated custom node
 */
export function getUniversalDefinition(customDef: any): UniversalNodeDefinition | null {
  if (isCustomNodeMigrated(customDef)) {
    return customDef._universalDefinition;
  }
  return null;
}

/**
 * Migrate a single custom node to UniversalNode format
 * Returns the updated definition with migration flags
 */
export function migrateSingleCustomNode(customDef: CustomNodeDefinition): any {
  try {
    const universalDef = convertCustomNodeToUniversal(customDef);
    
    return {
      ...customDef,
      _migrated: true,
      _universalDefinition: universalDef,
      _migrationDate: new Date().toISOString()
    };
  } catch (error) {
    logger.error('[Migration] Failed to migrate custom node:', {
      nodeName: customDef.name,
      error
    });
    throw error;
  }
}

/**
 * Migrate all custom nodes in localStorage to UniversalNode format
 */
export function migrateCustomNodesInStorage(): {
  migrated: number;
  skipped: number;
  failed: number;
  errors: string[];
} {
  const result = {
    migrated: 0,
    skipped: 0,
    failed: 0,
    errors: [] as string[]
  };
  
  try {
    const customNodes = CustomNodeStorage.getAll();
    
    logger.info('[Migration] Starting custom node migration:', {
      totalNodes: customNodes.length
    });
    
    for (const customDef of customNodes) {
      try {
        // Skip if already migrated
        if (isCustomNodeMigrated(customDef)) {
          logger.debug('[Migration] Skipping already migrated node:', customDef.name);
          result.skipped++;
          continue;
        }
        
        // Migrate the node
        const migratedDef = migrateSingleCustomNode(customDef);
        
        // Save back to storage
        CustomNodeStorage.save(migratedDef);
        
        logger.info('[Migration] Migrated custom node:', customDef.name);
        result.migrated++;
        
      } catch (error) {
        result.failed++;
        const errorMsg = `${customDef.name}: ${error instanceof Error ? error.message : String(error)}`;
        result.errors.push(errorMsg);
        logger.error('[Migration] Failed to migrate custom node:', {
          nodeName: customDef.name,
          error
        });
      }
    }
    
    logger.info('[Migration] Migration complete:', result);
    
  } catch (error) {
    const errorMsg = `Storage error: ${error instanceof Error ? error.message : String(error)}`;
    result.errors.push(errorMsg);
    logger.error('[Migration] Failed to access storage:', error);
  }
  
  return result;
}

/**
 * Check if migration is needed
 */
export function needsMigration(): boolean {
  try {
    const customNodes = CustomNodeStorage.getAll();
    return customNodes.some(node => !isCustomNodeMigrated(node));
  } catch (error) {
    logger.error('[Migration] Failed to check migration status:', error);
    return false;
  }
}

/**
 * Get migration statistics
 */
export function getMigrationStats(): {
  total: number;
  migrated: number;
  unmigrated: number;
  needsMigration: boolean;
} {
  try {
    const customNodes = CustomNodeStorage.getAll();
    const migratedCount = customNodes.filter(isCustomNodeMigrated).length;
    
    return {
      total: customNodes.length,
      migrated: migratedCount,
      unmigrated: customNodes.length - migratedCount,
      needsMigration: migratedCount < customNodes.length
    };
  } catch (error) {
    logger.error('[Migration] Failed to get migration stats:', error);
    return {
      total: 0,
      migrated: 0,
      unmigrated: 0,
      needsMigration: false
    };
  }
}
